// client/src/pages/WelcomePage.js — /welcome?token=… : the agent's invite link.
// The agent chooses their own password (12+ characters), then is told how to get the app.
// Server: routes/agentInvites.js publicRouter (/api/public/agent-welcome).
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const API = '/api/public/agent-welcome';

const WelcomePage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const [info, setInfo] = React.useState(null);
    const [password, setPassword] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(null);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!token) { setInfo({ valid: false, message: 'This link is missing its code. Use the whole link the managing broker gave you.' }); return; }
        fetch(`${API}?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
            .then((r) => r.json())
            .then(setInfo)
            .catch(() => setInfo({ valid: false, message: 'Could not check the link. Please try again in a moment.' }));
    }, [token]);

    const min = (info && info.min_password_length) || 12;
    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < min) return setError(`Your password must be at least ${min} characters.`);
        if (password !== confirm) return setError('The two passwords do not match.');
        setBusy(true);
        try {
            const resp = await fetch(API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword: password }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.message || 'Could not set your password.');
            setDone(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const inputCls = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 sm:text-sm';
    const twoFactor = (done && done.two_factor_required) || (info && info.two_factor_required);

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-100 px-4 py-8">
            <div className="w-full max-w-md p-6 sm:p-8 space-y-5 bg-white rounded-xl shadow-lg">
                <h1 className="text-2xl font-bold text-gray-900 text-center">Welcome to Business Real Estate Corp.</h1>

                {!info && <p className="text-sm text-slate-500 text-center">Checking your link…</p>}

                {info && !info.valid && !done && (
                    <div className="space-y-3">
                        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{info.message}</p>
                        <Link to="/portal" className="block text-center text-sm font-medium text-indigo-600 hover:underline">Go to sign in</Link>
                    </div>
                )}

                {info && info.valid && !done && (
                    <form className="space-y-4" onSubmit={submit}>
                        <p className="text-sm text-slate-700">
                            {info.first_name ? `Hello ${info.first_name}. ` : ''}Your username is <strong className="font-mono">{info.username}</strong>.
                            Choose your password to finish setting up your login.
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="welcome-pw">Password</label>
                            <input id="welcome-pw" type="password" autoComplete="new-password" required autoFocus className={inputCls}
                                value={password} onChange={(e) => setPassword(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">At least {min} characters. A short sentence is easy to remember.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="welcome-pw2">Type it again</label>
                            <input id="welcome-pw2" type="password" autoComplete="new-password" required className={inputCls}
                                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                        </div>
                        {error && <p className="text-sm text-red-600">{error}</p>}
                        <button type="submit" disabled={busy}
                            className="w-full flex justify-center py-2 px-4 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400">
                            {busy ? 'Saving…' : 'Set my password'}
                        </button>
                        <p className="text-xs text-slate-500">This link works once{info.expires_at ? `, until ${new Date(info.expires_at).toLocaleDateString()}` : ''}.</p>
                    </form>
                )}

                {done && (
                    <div className="space-y-4">
                        <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 text-sm">
                            Your password is set. Your username is <strong className="font-mono">{done.username}</strong>.
                        </p>
                        <div>
                            <h2 className="text-sm font-semibold text-slate-800">Next steps</h2>
                            <ol className="mt-2 space-y-2 text-sm text-slate-700 list-decimal ml-5">
                                <li><strong>Put the app on your phone.</strong> Open <a href="/app" className="text-indigo-700 underline">the BREC app page</a> on your phone and follow the steps for iPhone or Android. On Android you can also <a href="/app/android" className="text-indigo-700 underline">download the app</a> directly.</li>
                                {twoFactor
                                    ? <li><strong>Have your phone ready for two-factor sign-in.</strong> The first time you sign in you will be asked to set up a code app (such as Google Authenticator or Microsoft Authenticator). It takes a minute.</li>
                                    : <li><strong>Consider two-factor sign-in.</strong> After you sign in, open My Security to add a code from a phone app. It keeps your login safe even if someone learns your password.</li>}
                                <li><strong>Passkeys.</strong> If the sign-in page offers to sign you in with your fingerprint or face (a passkey), you can add one under My Security once you are signed in.</li>
                                <li><strong>Sign in.</strong> Your home page opens with Property search and Zoning search at the top.</li>
                            </ol>
                        </div>
                        <Link to="/portal" className="block text-center w-full py-2 px-4 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900">
                            Sign in now
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WelcomePage;
